import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Modal,
    Typography,
    CircularProgress,
    Button,
    Paper,
    Chip,
    Divider,
    IconButton,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { toast, ToastContainer } from "react-toastify";
import { decryptData } from "../../../crypto";
import PaperCredentialsModal from "./PaperCredentialsModal";
import useStyles from "./styles";

const getHardnessLevel = (q) => {
    if (!q) return "medium";

    const rawHardness =
        q.tagging?.difficulty_level ??
        q.tagging?.hardness_level ??
        q.tagging?.level ??
        q.other_taggings?.[0]?.difficulty_level;

    if (rawHardness === undefined || rawHardness === null || rawHardness === "") {
        return "medium";
    }

    const val = String(rawHardness).trim().toLowerCase();

    if (!val) return "medium";

    if (val === "1" || val.includes("easy") || val === "e") {
        return "easy";
    }
    if (val === "2" || val.includes("medium") || val.includes("moderate") || val === "m") {
        return "medium";
    }
    if (val === "3" || val.includes("hard") || val.includes("difficult") || val.includes("tough") || val === "h") {
        return "hard";
    }
    if (val === "4" || val.includes("expert")) {
        return "expert";
    }

    return val;
};

const parseInstructions = (instructionVal) => {
    if (!instructionVal) return "";
    let parsed = "";
    try {
        if (typeof instructionVal === "string") {
            const trimmed = instructionVal.trim();
            if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
                const jsonObj = JSON.parse(trimmed);
                if (typeof jsonObj === "object" && jsonObj !== null) {
                    /*
                    const parts = Object.values(jsonObj)
                        .map(val => (val ? String(val).trim() : ""))
                        .filter(val => val !== "");
                    parsed = parts.join("<br />");
                    */
                    const englishKey = Object.keys(jsonObj).find(k => k === "1" || k.toLowerCase() === "english");
                    if (englishKey) {
                        parsed = String(jsonObj[englishKey]).trim();
                    } else {
                        const parts = Object.entries(jsonObj)
                            .filter(([key]) => key !== "2" && key.toLowerCase() !== "hindi")
                            .map(([_, val]) => (val ? String(val).trim() : ""))
                            .filter(val => val !== "");
                        parsed = parts.join("<br />");
                    }
                } else {
                    parsed = String(jsonObj);
                }
            } else {
                parsed = trimmed;
            }
        } else if (typeof instructionVal === "object" && instructionVal !== null) {
            /*
            const parts = Object.values(instructionVal)
                .map(val => (val ? String(val).trim() : ""))
                .filter(val => val !== "");
            parsed = parts.join("<br />");
            */
            const englishKey = Object.keys(instructionVal).find(k => k === "1" || k.toLowerCase() === "english");
            if (englishKey) {
                parsed = String(instructionVal[englishKey]).trim();
            } else {
                const parts = Object.entries(instructionVal)
                    .filter(([key]) => key !== "2" && key.toLowerCase() !== "hindi")
                    .map(([_, val]) => (val ? String(val).trim() : ""))
                    .filter(val => val !== "");
                parsed = parts.join("<br />");
            }
        } else {
            parsed = String(instructionVal);
        }
    } catch (e) {
        console.warn("Failed to parse instructions:", e);
        parsed = String(instructionVal);
    }
    return parsed.trim();
};

const isJeeExam = (paperDetails) => {
    if (!paperDetails) return false;
    const stream = (paperDetails.stream || "").trim().toLowerCase();
    const pattern = (paperDetails.pattern || "").trim().toLowerCase();
    const name = (paperDetails.name || "").trim().toLowerCase();

    if (stream.includes("neet") || pattern.includes("neet") || name.includes("neet")) {
        return false;
    }

    return (
        stream === "jm" ||
        stream === "ja" ||
        stream === "iit" ||
        pattern.includes("jee") ||
        name.includes("jee") ||
        name.includes("main") ||
        name.includes("advance")
    );
};

const prependInstructionToQuestions = (questions, instText) => {
    if (!questions) return questions;
    const updated = {};
    for (const [qId, q] of Object.entries(questions)) {
        const qCopy = { ...q };
        if (qCopy.content) {
            const updatedContent = {};
            for (const [lang, langContent] of Object.entries(qCopy.content)) {
                const label = lang === "Hindi" ? "निर्देश:" : "Instruction:";
                let instHtml = "";
                if (instText) {
                    instHtml = `
<div class="section-instruction-box" style="margin-bottom: 16px; padding: 12px 20px 12px 32px; border-left: 4px solid #00577B; background-color: #f4f8fa; border-radius: 4px; font-size: 14px; line-height: 1.5; color: #333;">
    <div style="font-weight: 700; color: #00577B; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; font-size: 12px;">${label}</div>
    <div>${instText}</div>
</div>
`;
                }

                let groupHtml = "";
                if (qCopy.group_content) {
                    const rawGroup = qCopy.group_content[lang] || qCopy.group_content[lang.toLowerCase()] || "";
                    if (rawGroup && typeof rawGroup === "string" && rawGroup.trim() !== "") {
                        groupHtml = `<div class="group-content-box" style="margin-bottom: 16px;">${rawGroup.trim()}</div>`;
                    }
                }

                updatedContent[lang] = {
                    ...langContent,
                    qns_content: instHtml + groupHtml + (langContent.qns_content || "")
                };
            }
            qCopy.content = updatedContent;
        }
        updated[qId] = qCopy;
    }
    return updated;
};

export default function QpsyncJee({ onUpload }) {
    const { rowId } = useParams();
    const navigate = useNavigate();
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error'
    const [message, setMessage] = useState("Syncing question paper details...");
    const [paperData, setPaperData] = useState(null);

    const syncPaper = async (code, passkey) => {
        setLoading(true);
        setStatus(null);
        setMessage("Syncing question paper details...");
        try {
            const token = decryptData(sessionStorage.getItem("token"));
            if (!token) {
                throw new Error("Session expired. Please log in again.");
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/question/allen/get-questions-api`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        paper_code: code,
                        paper_passkey: passkey
                    })
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log("QP Sync Response Data (JEE):", data);

            let paperInfo = null;
            if (data && data.status === "success") {
                paperInfo = data.data;
            } else if (data && data.paper_details) {
                paperInfo = data;
            }

            if (paperInfo) {
                // Prepend section-wise instructions for JEE exams
                if (isJeeExam(paperInfo.paper_details) && paperInfo.paper_sections) {
                    paperInfo.paper_sections.forEach(section => {
                        const hasSub = Number(section.have_subsections) === 1;
                        if (hasSub && section.subsections) {
                            section.subsections.forEach(sub => {
                                const instText = parseInstructions(sub.instructions) || parseInstructions(section.instructions);
                                sub.questions = prependInstructionToQuestions(sub.questions, instText);
                            });
                        } else if (section.questions) {
                            const instText = parseInstructions(section.instructions);
                            section.questions = prependInstructionToQuestions(section.questions, instText);
                        }
                    });
                }

                setPaperData(paperInfo);
                setStatus("success");
                setMessage("Question Paper synced successfully!");
                toast.success("Question Paper synced successfully!");
            } else {
                throw new Error(data.msg || data.message || "Failed to sync question paper.");
            }
        } catch (err) {
            console.error("QP Sync Request Error (JEE):", err);
            setStatus("error");
            setMessage(err.message || "Failed to sync question paper.");
            toast.error(err.message || "Failed to sync question paper.");
        } finally {
            setLoading(false);
        }
    };

    const handleCredentialsSubmit = ({ paperCode, paperPasskey }) => {
        syncPaper(paperCode, paperPasskey);
    };

    const handleClose = () => {
        navigate(`/admin/addquestion/${rowId}`);
    };

    // -------- Helpers for Image to Base64 Conversion --------
    const makeUrlRelative = (url) => {
        if (url && url.startsWith("https://qb.allen.ac.in")) {
            return url.replace("https://qb.allen.ac.in", "");
        }
        return url;
    };

    const extractImageUrls = (html) => {
        if (!html) return [];
        const regex = /<img[^>]+src=["']([^"']+)["']/gi;
        const urls = [];
        let match;
        while ((match = regex.exec(html)) !== null) {
            urls.push(match[1]);
        }
        return urls;
    };

    const convertImageToBase64 = async (url) => {
        if (!url || typeof url !== "string") return url;
        if (url.startsWith("data:")) return url;

        let fullImageUrl = url.trim();
        if (!/^https?:\/\//i.test(fullImageUrl)) {
            fullImageUrl = `https://d2lbh14zkcqlst.cloudfront.net/${fullImageUrl.replace(/^\//, "")}`;
        }

        const blobToBase64 = (blob) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const result = reader.result;
                    if (typeof result === "string" && result.startsWith("data:image/")) {
                        resolve(result);
                    } else {
                        reject(new Error("Not an image base64"));
                    }
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        };

        // Strategy 1: Direct fetch
        try {
            const res = await fetch(fullImageUrl);
            const contentType = (res.headers.get("content-type") || "").toLowerCase();
            if (res.ok && contentType.startsWith("image/")) {
                const blob = await res.blob();
                if (blob && blob.type && blob.type.toLowerCase().startsWith("image/")) {
                    const b64 = await blobToBase64(blob);
                    if (b64) return b64;
                }
            }
        } catch (e) {
            // Direct fetch CORS blocked
        }

        // Strategy 2: Image Proxy CDN (weserv.nl)
        try {
            const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(fullImageUrl)}`;
            const res = await fetch(proxyUrl);
            const contentType = (res.headers.get("content-type") || "").toLowerCase();
            if (res.ok && contentType.startsWith("image/")) {
                const blob = await res.blob();
                const b64 = await blobToBase64(blob);
                if (b64) return b64;
            }
        } catch (e) {
            // Weserv proxy failed
        }

        // Strategy 3: CORS Proxy
        try {
            const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(fullImageUrl)}`;
            const res = await fetch(proxyUrl);
            const contentType = (res.headers.get("content-type") || "").toLowerCase();
            if (res.ok && (contentType.startsWith("image/") || contentType.includes("octet-stream"))) {
                const blob = await res.blob();
                const b64 = await blobToBase64(blob);
                if (b64) return b64;
            }
        } catch (e) {
            // Corsproxy failed
        }

        // Strategy 4: Canvas fallback
        try {
            return await new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = () => {
                    try {
                        const canvas = document.createElement("canvas");
                        canvas.width = img.naturalWidth || img.width || 300;
                        canvas.height = img.naturalHeight || img.height || 150;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(img, 0, 0);
                        const dataUrl = canvas.toDataURL("image/png");
                        if (dataUrl && dataUrl.startsWith("data:image/")) {
                            resolve(dataUrl);
                        } else {
                            resolve(fullImageUrl);
                        }
                    } catch (err) {
                        resolve(fullImageUrl);
                    }
                };
                img.onerror = () => resolve(fullImageUrl);
                img.src = fullImageUrl;
            });
        } catch (err) {
            return fullImageUrl;
        }
    };

    const convertAllImagesInHtmlToBase64 = async (htmlContent) => {
        if (!htmlContent || typeof htmlContent !== "string") return htmlContent;
        if (!htmlContent.includes("<img")) return htmlContent;

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, "text/html");
            const imgTags = doc.querySelectorAll("img");

            for (const img of imgTags) {
                const src = img.getAttribute("src");
                if (src && !src.startsWith("data:")) {
                    const base64 = await convertImageToBase64(src);
                    img.setAttribute("src", base64);
                }
                img.style.maxWidth = "100%";
                img.style.maxHeight = "250px";
                img.style.height = "auto";
                img.style.objectFit = "contain";
            }

            return doc.body.innerHTML;
        } catch (e) {
            return htmlContent;
        }
    };

    const processDataWithBase64 = async (data, onProgress) => {
        if (!data) return data;
        const processed = JSON.parse(JSON.stringify(data));

        const processQuestions = async (questions) => {
            for (const qKey of Object.keys(questions || {})) {
                const q = questions[qKey];
                if (q.group_content) {
                    for (const langKey of Object.keys(q.group_content || {})) {
                        q.group_content[langKey] = await convertAllImagesInHtmlToBase64(q.group_content[langKey]);
                    }
                }
                for (const langKey of Object.keys(q.content || {})) {
                    const lang = q.content[langKey];
                    lang.qns_content = await convertAllImagesInHtmlToBase64(lang.qns_content);
                    if (Array.isArray(lang.options)) {
                        const newOpts = [];
                        for (const opt of lang.options) {
                            newOpts.push(await convertAllImagesInHtmlToBase64(opt));
                        }
                        lang.options = newOpts;
                    }
                    if (Array.isArray(lang.display_options)) {
                        const newDisp = [];
                        for (const opt of lang.display_options) {
                            newDisp.push(await convertAllImagesInHtmlToBase64(opt));
                        }
                        lang.display_options = newDisp;
                    }
                    lang.solution = await convertAllImagesInHtmlToBase64(lang.solution);
                }
            }
        };

        const sections = processed.paper_sections || [];
        let sectionIndex = 0;
        for (const section of sections) {
            sectionIndex++;
            onProgress && onProgress(`Converting images to base64 for section ${sectionIndex}/${sections.length}...`);

            if (section.instructions) {
                section.instructions = await convertAllImagesInHtmlToBase64(section.instructions);
            }

            if (Number(section.have_subsections) === 1) {
                for (const sub of section.subsections || []) {
                    if (sub.instructions) {
                        sub.instructions = await convertAllImagesInHtmlToBase64(sub.instructions);
                    }
                    await processQuestions(sub.questions);
                }
            } else {
                await processQuestions(section.questions);
            }
        }

        return processed;
    };

    const checkOptionCorrectness = (answer, index) => {
        if (!answer) return false;
        return answer.includes(`[${index}]`);
    };

    const handleUpload = async () => {
        if (!paperData) {
            toast.error("No paper data to upload.");
            return;
        }

        const token = decryptData(sessionStorage.getItem("token"));
        if (!token) {
            toast.error("Session expired. Please log in again.");
            return;
        }

        setLoading(true);
        setMessage("Converting paper images to base64...");

        let processedPaper = paperData;
        try {
            processedPaper = await processDataWithBase64(paperData, (progressMsg) => setMessage(progressMsg));
        } catch (err) {
            console.error("Base64 image conversion failed:", err);
            toast.warning("Some images failed to convert to base64.");
        }

        setMessage("Fetching exam details for subject mapping...");

        let subjects = [];
        try {
            const examRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/${rowId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (examRes.ok) {
                const examJson = await examRes.json();
                if (examJson && examJson.exam) {
                    subjects = examJson.exam.subjects || [];
                }
            }
        } catch (err) {
            console.error("Failed to fetch exam subjects:", err);
        }

        const subjectsSectionsMap = {};
        if (subjects.length > 0) {
            setMessage("Fetching subject sections...");
            try {
                await Promise.all(
                    subjects.map(async (sub) => {
                        const secRes = await fetch(
                            `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/subject_section/subject_id/${sub.id}`,
                            {
                                headers: {
                                    Authorization: `Bearer ${token}`
                                }
                            }
                        );
                        if (secRes.ok) {
                            const secJson = await secRes.json();
                            if (secJson && secJson.success && secJson.subjects_section) {
                                subjectsSectionsMap[sub.id] = secJson.subjects_section;
                            }
                        }
                    })
                );
            } catch (err) {
                console.error("Failed to fetch subject sections:", err);
            }
        }

        const matchJeeMainsSection = (allenSubjectName, allenSectionName) => {
            const normalizeString = (str) => (str || "").replace(/[\s\-_]+/g, "").toLowerCase();

            const targetSub = normalizeString(allenSubjectName);
            const targetSec = normalizeString(allenSectionName);

            // 1. Exact match on normalized api_subject_name and api_section_name
            for (const sub of subjects) {
                const sections = subjectsSectionsMap[sub.id] || [];
                for (const sec of sections) {
                    const apiSub = normalizeString(sec.api_subject_name);
                    const apiSec = normalizeString(sec.api_section_name);
                    if (apiSub === targetSub && apiSec === targetSec) {
                        return {
                            subjectId: sub.id,
                            sectionId: sec.id
                        };
                    }
                }
            }

            // 2. Substring match fallback on normalized values (Commented out to prevent incorrect fallback mapping when api_section_name/api_subject_name are missing)
            /*
            for (const sub of subjects) {
                const sections = subjectsSectionsMap[sub.id] || [];
                for (const sec of sections) {
                    const apiSub = normalizeString(sec.api_subject_name);
                    const apiSec = normalizeString(sec.api_section_name);
                    if (
                        (apiSub.includes(targetSub) || targetSub.includes(apiSub)) &&
                        (apiSec.includes(targetSec) || targetSec.includes(apiSec))
                    ) {
                        return {
                            subjectId: sub.id,
                            sectionId: sec.id
                        };
                    }
                }
            }
            */
            return null;
        };

        // Step 1: Collect all sections/subsections to validate
        const sectionsToValidate = [];
        for (const section of processedPaper.paper_sections || []) {
            const hasSubsections = Number(section.have_subsections) === 1;

            if (hasSubsections) {
                for (const sub of section.subsections || []) {
                    sectionsToValidate.push({
                        subjectName: section.name,
                        sectionName: sub.name,
                        displayName: `section "${sub.name}" under subject "${section.name}"`
                    });
                }
            } else {
                sectionsToValidate.push({
                    subjectName: section.name,
                    sectionName: section.name,
                    displayName: `subject/section "${section.name}"`
                });
            }
        }

        // Step 2: Validate collected sections
        for (const item of sectionsToValidate) {
            const match = matchJeeMainsSection(item.subjectName, item.sectionName);
            if (!match || !match.sectionId) {
                toast.error(`Import Mismatch: The imported ${item.displayName} does not match any subject-section configured in the database. Please verify your exam settings.`);
                setLoading(false);
                return;
            }
        }

        const payloadList = [];
        let questionCounter = 1;

        for (const section of processedPaper.paper_sections || []) {
            const processQuestions = (questions, secId, subId) => {
                for (const q of Object.values(questions || {})) {
                    let mappedQuestionType = "Single";
                    const rawType = q.question_type || "";
                    const enContent = q.content?.English || {};
                    const hiContent = q.content?.Hindi || {};
                    const hasOptions = (enContent.options || []).length > 0;

                    if (hasOptions) {
                        const rawTypeLower = rawType.toLowerCase();
                        const isMultipleChoice =
                            (rawTypeLower.includes("multiple") &&
                                !rawTypeLower.includes("example") &&
                                !rawTypeLower.includes("single")) ||
                            (enContent.answer && enContent.answer.includes(","));

                        if (isMultipleChoice) {
                            mappedQuestionType = "Multiple Choice";
                        } else {
                            mappedQuestionType = "Single";
                        }
                    } else {
                        const ansStr = enContent.answer || "";
                        if (ansStr.includes("-") && ansStr.split("-").length === 2 && !isNaN(ansStr.split("-")[0].trim()) && !isNaN(ansStr.split("-")[1].trim())) {
                            mappedQuestionType = "Range";
                        } else {
                            mappedQuestionType = "Fill In The Blanks";
                        }
                    }

                    let mappedOptions = [];
                    if (mappedQuestionType === "Fill In The Blanks" || mappedQuestionType === "Range") {
                        mappedOptions = [{
                            option_en: "",
                            option_hi: "",
                            correct_option: true,
                            solution_en: enContent.answer || "",
                            solution_hi: hiContent.answer || enContent.answer || "",
                            description_en: "",
                            description_hi: "",
                            q_prifix: 1
                        }];
                    } else {
                        const maxLen = Math.max((enContent.options || []).length, (hiContent.options || []).length);
                        for (let i = 0; i < maxLen; i++) {
                            const optEn = enContent.options?.[i] || "";
                            const optHi = hiContent.options?.[i] || "";

                            const isCorrectEn = enContent.answer ? checkOptionCorrectness(enContent.answer, i) : false;
                            const isCorrectHi = hiContent.answer ? checkOptionCorrectness(hiContent.answer, i) : isCorrectEn;
                            const isCorrect = isCorrectEn || isCorrectHi;

                            const solEn = isCorrect ? (enContent.solution || "") : "";
                            const solHi = isCorrect ? (hiContent.solution || enContent.solution || "") : "";

                            mappedOptions.push({
                                option_en: optEn,
                                option_hi: optHi,
                                correct_option: isCorrect,
                                solution_en: solEn,
                                solution_hi: solHi,
                                description_en: "",
                                description_hi: "",
                                q_prifix: i + 1
                            });
                        }
                    }

                    const questionPayload = {
                        question: {
                            exam_id: rowId,
                            question_type: mappedQuestionType,
                            hardness_level: getHardnessLevel(q),
                            subject_id: subId,
                            mark: Number(q.marks) || 0,
                            code_language: "",
                            public_input: "",
                            private_input: "",
                            negative_marking: Number(q.neg_marks) > 0 ? "yes" : "no",
                            negative_mark: -Math.abs(Number(q.neg_marks)) || 0,
                            ...(mappedQuestionType === "Multiple Choice" && {
                                partial_mark: Number(q.partial_correct_marks) || 0,
                            }),
                            question_en: enContent.qns_content || "",
                            question_hi: hiContent.qns_content || "",
                            q_prifix: questionCounter,
                            type: "normal",
                            paragraph_en: "",
                            paragraph_hi: "",
                            section_id: secId
                        },
                        options: mappedOptions
                    };

                    payloadList.push(questionPayload);
                    questionCounter++;
                }
            };

            if (Number(section.have_subsections) === 1) {
                for (const sub of section.subsections || []) {
                    const match = matchJeeMainsSection(section.name, sub.name);
                    const subId = match ? match.subjectId : "";
                    const secId = match ? match.sectionId : "";
                    processQuestions(sub.questions, secId, subId);
                }
            } else {
                const match = matchJeeMainsSection(section.name, section.name);
                const subId = match ? match.subjectId : "";
                const secId = match ? match.sectionId : "";
                processQuestions(section.questions, secId, subId);
            }
        }

        console.log("FINAL CONSTRUCTED MAPPED QUESTIONS PAYLOAD ARRAY (JEE):", payloadList);

        setMessage("Uploading questions to backend...");

        try {
            const syncJson = await onUpload(payloadList, paperData?.paper_details?.paper_code);
            if (syncJson.status === "success" || syncJson.success) {
                toast.success("Questions uploaded and synced successfully!");
                navigate(`/admin/addquestion/${rowId}`);
            } else {
                throw new Error(syncJson.message || syncJson.msg || "Failed to upload synced questions.");
            }
        } catch (err) {
            console.error("Upload sync failed:", err);
            toast.error(err.message || "Failed to upload synced questions.");
        } finally {
            setLoading(false);
        }
    };

    const renderQuestion = (q, index) => {
        const languages = Object.keys(q.content || {}).filter(
            (lang) => q.content[lang] && q.content[lang].qns_content
        );

        return (
            <div key={q.id} className={classes.questionCard}>
                <div className={classes.questionHeader}>
                    <Typography className={classes.qNumber}>
                        Q{q.sqn_no || index + 1}
                    </Typography>
                    <div className={classes.marksInfo}>
                        <Chip size="small" label={`+${q.marks}`} className={classes.chipPositive} />
                        <Chip size="small" label={`-${q.neg_marks}`} className={classes.chipNegative} />
                        {Number(q.partial_correct_marks) > 0 && (
                            <Chip
                                size="small"
                                label={`Partial +${q.partial_correct_marks}`}
                                className={classes.chipPartial}
                            />
                        )}
                        {Number(q.partial_correct_neg_marks) > 0 && (
                            <Chip
                                size="small"
                                label={`Partial -${q.partial_correct_neg_marks}`}
                                className={classes.chipPartial}
                            />
                        )}
                        <Chip size="small" variant="outlined" label={q.question_type} />
                    </div>
                </div>

                {languages.map((lang) => {
                    const c = q.content[lang];
                    return (
                        <div key={lang} className={classes.langBlock}>
                            <Typography className={classes.langLabel}>{lang}</Typography>
                            <div
                                className={classes.qnsContent}
                                dangerouslySetInnerHTML={{ __html: c.qns_content }}
                            />
                            {(c.options || []).length > 0 ? (
                                <div className={classes.optionsGrid}>
                                    {c.options.map((opt, i) => (
                                        <div
                                            key={i}
                                            className={`${classes.optionItem} ${c.answer === `[${i}]` ? classes.correctOption : ""
                                                }`}
                                        >
                                            <span className={classes.optionLabel}>
                                                {String.fromCharCode(65 + i)}.
                                            </span>
                                            <span dangerouslySetInnerHTML={{ __html: opt }} />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                c.answer && (
                                    <div className={classes.fillBlankAnswer}>
                                        <span style={{ fontWeight: 700, color: "#00577B" }}>Answer:</span>{" "}
                                        <span style={{ fontWeight: 600, color: "#2e7d32" }}>{c.answer}</span>
                                    </div>
                                )
                            )}
                        </div>
                    );
                })}
                <Divider style={{ margin: "12px 0" }} />
            </div>
        );
    };

    const renderSection = (section) => {
        const hasSub = Number(section.have_subsections) === 1;

        return (
            <Paper key={section.id} className={classes.subjectPaper} elevation={2}>
                <div className={classes.subjectHeader}>
                    <Typography variant="h6">{section.name}</Typography>
                    <Chip
                        label={`Total Questions: ${section.total_questions}`}
                        className={classes.totalChip}
                    />
                </div>

                {hasSub
                    ? section.subsections.map((sub) => (
                        <div key={sub.id} className={classes.subsectionBlock}>
                            <div className={classes.sectionHeader}>
                                <Typography variant="subtitle1">{sub.name}</Typography>
                                <Chip
                                    size="small"
                                    label={`Questions: ${sub.total_questions}`}
                                    className={classes.totalChipSmall}
                                />
                            </div>
                            {Object.values(sub.questions || {}).map((q, idx) =>
                                renderQuestion(q, idx)
                            )}
                        </div>
                    ))
                    : Object.values(section.questions || {}).map((q, idx) =>
                        renderQuestion(q, idx)
                    )}
            </Paper>
        );
    };

    const renderPaperDetails = () => {
        if (!paperData) return null;
        const d = paperData.paper_details;
        return (
            <div className={classes.detailRow}>
                <Chip label={`Code: ${d.paper_code}`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Max Marks: ${d.max_marks}`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Duration: ${d.total_time} min`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Questions: ${d.number_of_questions}`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Pattern: ${d.pattern}`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Type: ${d.test_type}`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Stream: ${d.stream}`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Date: ${d.test_date}`} variant="outlined" size="small" className={classes.detailChip} />
                <Chip label={`Language: ${d.language}`} variant="outlined" size="small" className={classes.detailChip} />
            </div>
        );
    };

    if (!loading && status === null && !paperData) {
        return (
            <PaperCredentialsModal
                open={true}
                onClose={handleClose}
                onSubmit={handleCredentialsSubmit}
            />
        );
    }

    return (
        <Modal
            open={true}
            onClose={handleClose}
            className={classes.modal}
            disableBackdropClick
        >
            {loading || status === "error" ? (
                <div className={classes.paperSmall}>
                    <ToastContainer position="top-right" autoClose={5000} style={{ zIndex: 99999 }} />
                    <Typography variant="h5" className={classes.title}>
                        Sync Question Paper (JEE Mains)
                    </Typography>

                    {loading ? (
                        <div className={classes.loaderContainer}>
                            <CircularProgress style={{ color: "#00577B", width: 50, height: 50 }} />
                            <Typography variant="body1" className={classes.text}>
                                {message}
                            </Typography>
                        </div>
                    ) : (
                        <>
                            <CancelIcon className={classes.statusIconError} />
                            <Typography variant="body1" className={classes.text}>
                                {message}
                            </Typography>
                            <div style={{ display: "flex", gap: "16px", width: "100%", justifyContent: "center" }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStatus(null);
                                        setLoading(false);
                                    }}
                                    style={{ textTransform: "none", borderRadius: "8px", flex: 1 }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    className={`${classes.button} ${classes.btnPrimary}`}
                                    onClick={handleClose}
                                    style={{ flex: 1 }}
                                >
                                    Close
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className={classes.paperLarge}>
                    <ToastContainer position="top-right" autoClose={5000} style={{ zIndex: 99999 }} />
                    <div className={classes.popupHeader}>
                        <div className={classes.headerLeft}>
                            <Typography className={classes.titleSmall}>
                                {paperData?.paper_details?.name || "Synced Paper"}
                            </Typography>
                            <Typography className={classes.subtitle}>
                                {paperData?.paper_details?.number_of_questions} Questions • {paperData?.paper_details?.max_marks} Marks • {paperData?.paper_details?.total_time} min
                            </Typography>
                        </div>
                        <div className={classes.headerActions}>
                            <Button
                                variant="contained"
                                className={`${classes.button} ${classes.btnUpload}`}
                                onClick={handleUpload}
                                startIcon={<CloudUploadIcon />}
                            >
                                Upload
                            </Button>
                            <IconButton onClick={handleClose} size="small" title="Close">
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </div>

                    <div className={classes.popupBody}>
                        {renderPaperDetails()}
                        {paperData?.paper_sections?.map((section) => renderSection(section))}
                    </div>
                </div>
            )}
        </Modal>
    );
}
